import { isValidPluginPermission, type PluginPermission } from '@fias/arche-sdk';
import type { VirtualFileSystem } from '../types';

/**
 * Extract the plugin's declared permissions from its in-progress
 * `fias-plugin.json` manifest in the builder's virtual file system.
 *
 * The builder preview bridge is constructed with these so the preview enforces
 * capability gating identically to a deployed arche. Previously the preview
 * granted a hardcoded superset of permissions, so a plugin that called e.g.
 * `useFiasUser()` without declaring `user:profile:read` worked in preview but
 * hung on an infinite loading state in production. Feeding the *real* declared
 * set makes that failure reproduce in preview, before publish.
 *
 * Returns an empty list when the manifest is missing, unparseable, or declares
 * no valid permissions — which correctly yields a maximally-restricted preview
 * (the honest representation of a plugin that has declared nothing).
 */
export function extractDeclaredPermissions(files: VirtualFileSystem | null): PluginPermission[] {
  const manifest = files?.['fias-plugin.json'];
  if (!manifest?.content) return [];

  let parsed: unknown;
  try {
    parsed = JSON.parse(manifest.content);
  } catch {
    return [];
  }

  if (!parsed || typeof parsed !== 'object') return [];
  const raw = (parsed as { permissions?: unknown }).permissions;
  if (!Array.isArray(raw)) return [];

  const seen = new Set<PluginPermission>();
  for (const entry of raw) {
    if (typeof entry === 'string' && isValidPluginPermission(entry)) {
      seen.add(entry);
    }
  }
  return [...seen];
}
